import { Plus, X } from 'lucide-react';
import { Button, Input, Label } from '@zafroi/ui-kit';
import type { BotDomains, BotKnowledgeLink } from '@/types/engage';

/**
 * Reference-link editor for a knowledge entry.
 *
 * Single registered domain (or none) → one URL field per link, exactly as
 * before. Multiple registered domains → one URL field PER domain, so a
 * tenant whose sites use different paths across domains (e.g. mavk.com vs
 * mavk.in) can give each its own URL. The primary domain's field maps to
 * the link's canonical `url`; the rest map to `domainUrls[]`. The bot then
 * serves each visitor the URL for the domain they're on.
 */
export function LinkEditor({
  links,
  onChange,
  domains,
}: {
  links: BotKnowledgeLink[];
  onChange: (links: BotKnowledgeLink[]) => void;
  domains?: BotDomains;
}) {
  const domainList = domains?.domains ?? [];
  const primary = domains?.primary ?? domainList[0] ?? null;
  const multiDomain = domainList.length > 1;

  const urlForDomain = (link: BotKnowledgeLink, domain: string): string =>
    primary && domain === primary
      ? link.url
      : link.domainUrls?.find((d) => d.domain === domain)?.url ?? '';

  const setLabel = (i: number, value: string) =>
    onChange(links.map((l, idx) => (idx === i ? { ...l, label: value } : l)));

  // Single-domain / fallback URL field — edits the canonical `url`.
  const setPrimaryUrl = (i: number, value: string) =>
    onChange(links.map((l, idx) => (idx === i ? { ...l, url: value } : l)));

  const setDomainUrl = (i: number, domain: string, value: string) =>
    onChange(
      links.map((link, idx) => {
        if (idx !== i) return link;
        if (primary && domain === primary) return { ...link, url: value };
        const others = (link.domainUrls ?? []).filter((d) => d.domain !== domain);
        const next = value.trim() ? [...others, { domain, url: value }] : others;
        return { ...link, domainUrls: next.length ? next : undefined };
      }),
    );

  const addLink = () => onChange([...links, { label: '', url: '' }]);
  const removeLink = (i: number) => onChange(links.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-1.5">
      <Label>Reference links (optional)</Label>
      {multiDomain && (
        <p className="text-xs text-muted-foreground">
          Your widget runs on multiple domains. Enter the URL for each — the bot
          shows every visitor the link for the domain they&apos;re on.
        </p>
      )}
      {links.map((l, i) => (
        <div key={i} className="space-y-1.5 rounded-md border border-input p-2">
          <div className="flex items-center gap-1.5">
            <Input
              value={l.label}
              onChange={(e) => setLabel(i, e.target.value)}
              placeholder="Label (e.g. Returns policy)"
              className="flex-1"
            />
            <Button
              type="button"
              size="icon"
              variant="ghost"
              onClick={() => removeLink(i)}
              aria-label="Remove link"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          {multiDomain ? (
            domainList.map((domain) => (
              <div key={domain} className="flex items-center gap-1.5">
                <span
                  className="w-32 shrink-0 truncate text-xs text-muted-foreground"
                  title={domain}
                >
                  {domain}
                  {primary === domain ? ' · primary' : ''}
                </span>
                <Input
                  value={urlForDomain(l, domain)}
                  onChange={(e) => setDomainUrl(i, domain, e.target.value)}
                  placeholder="https://…"
                  className="flex-1"
                />
              </div>
            ))
          ) : (
            <Input
              value={l.url}
              onChange={(e) => setPrimaryUrl(i, e.target.value)}
              placeholder="https://…"
            />
          )}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={addLink}>
        <Plus className="h-3.5 w-3.5" /> Add link
      </Button>
    </div>
  );
}
